/**
 * Captures where a visitor came from (Meta ads, Google, direct, ...) on their
 * first landing and keeps it around long enough to attach to a later Purchase.
 *
 * Meta's own attribution relies on the _fbc / _fbp cookies the pixel sets from
 * `fbclid`. This module is the *reportable* layer on top of that: it stores the
 * campaign params so we can send them as event parameters and break the numbers
 * down by campaign / ad inside Events Manager.
 */

const STORAGE_KEY = 'immersia_attribution';
const SESSION_FLAG_KEY = 'immersia_ad_visit_sent';

/** Meta's click-through attribution window. */
const ATTRIBUTION_TTL_MS = 28 * 24 * 60 * 60 * 1000;

export interface Attribution {
  fbclid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  ad_id?: string;
  adset_id?: string;
  campaign_id?: string;
  referrer?: string;
  landing_page?: string;
  is_meta_traffic: boolean;
  captured_at: number;
}

const META_SOURCES = ['facebook', 'fb', 'instagram', 'ig', 'meta', 'messenger', 'audiencenetwork'];

const safeRead = (key: string, store: Storage): string | null => {
  try {
    return store.getItem(key);
  } catch {
    return null;
  }
};

const safeWrite = (key: string, value: string, store: Storage) => {
  try {
    store.setItem(key, value);
  } catch {
    /* storage disabled (private mode / quota) — attribution is best-effort */
  }
};

const looksLikeMeta = (source: string | undefined, referrer: string, hasFbclid: boolean) => {
  if (hasFbclid) return true;
  if (source && META_SOURCES.indexOf(source.toLowerCase()) !== -1) return true;
  return /facebook\.com|fb\.com|instagram\.com|fb\.me/i.test(referrer);
};

/**
 * Reads campaign params off the current URL and persists them. Existing
 * attribution is only overwritten when the new visit actually carries campaign
 * params, so an internal reload never wipes the original source.
 */
export const captureAttribution = (): Attribution | null => {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const get = (key: string) => params.get(key) || undefined;

  const fbclid = get('fbclid');
  const utm_source = get('utm_source');
  const referrer = document.referrer || '';

  const hasCampaignParams = Boolean(
    fbclid || utm_source || get('utm_campaign') || get('utm_medium')
  );

  if (!hasCampaignParams) {
    const existing = getAttribution();
    if (existing) return existing;
  }

  const attribution: Attribution = {
    fbclid,
    utm_source,
    utm_medium: get('utm_medium'),
    utm_campaign: get('utm_campaign'),
    utm_content: get('utm_content'),
    utm_term: get('utm_term'),
    // Meta appends these when dynamic URL params are configured on the ad
    ad_id: get('ad_id'),
    adset_id: get('adset_id'),
    campaign_id: get('campaign_id'),
    referrer,
    landing_page: window.location.pathname,
    is_meta_traffic: looksLikeMeta(utm_source, referrer, Boolean(fbclid)),
    captured_at: Date.now(),
  };

  safeWrite(STORAGE_KEY, JSON.stringify(attribution), window.localStorage);
  return attribution;
};

/** Returns stored attribution, or null when absent or past the 28-day window. */
export const getAttribution = (): Attribution | null => {
  if (typeof window === 'undefined') return null;

  const raw = safeRead(STORAGE_KEY, window.localStorage);
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw) as Attribution;
    if (!parsed.captured_at || Date.now() - parsed.captured_at > ATTRIBUTION_TTL_MS) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

/**
 * Flattens attribution into event parameters. Meta ignores unknown keys on
 * standard events but keeps them queryable on custom events, so these are what
 * make the "which ad drove this?" breakdown possible.
 */
export const getAttributionParams = (): Record<string, string> => {
  const attribution = getAttribution();
  if (!attribution) return { traffic_source: 'direct' };

  const params: Record<string, string> = {
    traffic_source: attribution.is_meta_traffic
      ? 'meta_ads'
      : attribution.utm_source || (attribution.referrer ? 'referral' : 'direct'),
  };

  if (attribution.utm_source) params.utm_source = attribution.utm_source;
  if (attribution.utm_medium) params.utm_medium = attribution.utm_medium;
  if (attribution.utm_campaign) params.utm_campaign = attribution.utm_campaign;
  if (attribution.utm_content) params.utm_content = attribution.utm_content;
  if (attribution.utm_term) params.utm_term = attribution.utm_term;
  if (attribution.ad_id) params.ad_id = attribution.ad_id;
  if (attribution.adset_id) params.adset_id = attribution.adset_id;
  if (attribution.campaign_id) params.campaign_id = attribution.campaign_id;
  if (attribution.landing_page) params.landing_page = attribution.landing_page;

  return params;
};

export const isMetaTraffic = (): boolean => Boolean(getAttribution()?.is_meta_traffic);

/** True once per browser session, so the ad-visit event isn't sent on every route change. */
export const claimAdVisitOncePerSession = (): boolean => {
  if (typeof window === 'undefined') return false;
  if (safeRead(SESSION_FLAG_KEY, window.sessionStorage)) return false;
  safeWrite(SESSION_FLAG_KEY, '1', window.sessionStorage);
  return true;
};
